import { CdkPortalOutlet, ComponentPortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ComponentRef, EventEmitter, Inject, OnDestroy, OnInit, Output, QueryList, signal, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatAccordion, MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, Observable, of } from 'rxjs';
import { DynFormSection } from '../../../model/dyn-form/dyn-form-section.model';
import { DynFormBase } from '../../../model/dyn-form/dyn-form.model';
import { ItemTypeMetadata } from '../../../model/dyn-form/form-metadata/item-type-metadata.enum';
import { ItemMetadataBase } from '../../../model/dyn-form/form-metadata/item/item-metadata.model';
import { LayoutTypeMetadata } from '../../../model/dyn-form/form-metadata/layout-type-metadata.enum';
import { RowLayoutMetadataBase } from '../../../model/dyn-form/form-metadata/layout/row-layout.metadata.model';
import { HasShowHideLogic } from '../../../model/dyn-form/form-metadata/logic/has-show-hide-logic.model';
import { ShowHideLogicMetadata, ShowHideLogicMetadataBase } from '../../../model/dyn-form/form-metadata/logic/show-hide-logic-metadata.model';
import { EForm, EFormHeaderAndOneSectionModelValue, EFormHeaderModelValue, EFormRegistry, EFormSection, EFormSectionModelValue, EFormSectionModelValueResponse, EFormSectionVisibility, FormValidationErrorController, SectionMetadata, StoreJob } from '../../../model/eform/eform-api.model';
import { ValidationSectionBase } from '../../../model/eform/scripts/validation-section.model';
import { ProcedureInstanceBase } from '../../../model/procedure-instance/procedure-instance.model';
import { DefaultSerializer } from '../../../operators/changes-serializer/changes-serializer.operator';
import { EFormDynFormService } from '../../../services/eform/e-form-dyn-form.service';
import { EFormFactoryService } from '../../../services/eform/e-form-factory.service';
import { EFormSectionMetadataService } from '../../../services/eform/e-form-section-metadata.service';
import { EFormValueService } from '../../../services/eform/e-form-value.service';
import { EFormScriptsService } from '../../../services/eform/scripts/e-form-scripts.service';
import { response } from '../../../services/error/error-functions.model';
import { ProcedureInstanceVisibilityService } from '../../../services/procedure-instance/procedure-instance-visibility.service';

@Component({
	selector: 'app-e-form-default',
	templateUrl: './e-form-default.component.html',
	styleUrls: ['./e-form-default.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		CdkPortalOutlet,
		MatAccordion, MatExpansionModule
	]
})
export class EFormDefaultComponent implements EForm, OnInit, AfterViewInit, OnDestroy {

	@ViewChildren(CdkPortalOutlet) viewChildren!: QueryList<CdkPortalOutlet>

	@ViewChildren(MatExpansionPanel) accordionItems!: QueryList<MatExpansionPanel>

	currentForm = signal<DynFormBase | null>(null)
	formSectionMetadataState = signal<SectionMetadata[]>([])
	openedComponentSection = signal<(ComponentPortal<EFormSection> | null)>(null)
	openedDynFormSection = signal<DynFormSection | null>(null)
	openedEFormSection = signal<EFormSection[]>([])

	possibleChangeVisibilitySection = signal<Map<string, { sectionId: number, logic: (HasShowHideLogic & ItemMetadataBase)[] }>>(new Map())

	valResult: ValidationSectionBase | null = null

	@Output()
	isSaving: EventEmitter<boolean> = new EventEmitter()

	@Output()
	saveResult: EventEmitter<{ ok: boolean; messageCode: string; }> = new EventEmitter()


	constructor(@Inject("CONTAINER_DATA") public data: {
		registry: EFormRegistry,
		procedureInstance: ProcedureInstanceBase,
		sectionErrors: FormValidationErrorController
	},
		protected dynFormService: EFormDynFormService,
		protected procedureInstanceVisibilityService: ProcedureInstanceVisibilityService,
		protected eFormFactoryService: EFormFactoryService,
		protected eFormSectionMetadataService: EFormSectionMetadataService,
		protected eFormValueService: EFormValueService,
		protected eFormScriptsService: EFormScriptsService,
		protected snackBar: MatSnackBar,
		protected dialog: MatDialog) {

	}

	ngAfterViewInit(): void {
		this.viewChildren.changes.subscribe(c => {
			let currentShowed: EFormSection[] = []
			this.viewChildren.forEach(e => {

				let section: EFormSection = (<ComponentRef<EFormSection>>e.attachedRef).instance
				currentShowed.push(section)

				let ds: DefaultSerializer<StoreJob, { ok: boolean, messageCode: string }> = new DefaultSerializer<StoreJob, { ok: boolean, messageCode: string }>()

				this.subscribeNextEvent(section);

				section.valueChanges.subscribe(ch => {
					ds.serialize(ch, this.executeStoreJob)
				})

				ds.state().subscribe(c => {
					this.isSaving.emit(c)
				})

				ds.operationResults().subscribe(r => {
					this.saveResult.emit(r)
				})

			})
			//TODO:Clear the old opened sections
			this.openedEFormSection.set(currentShowed)

		})

		//Open the first section if all is closed
		this.accordionItems.changes.subscribe(c => {

			//if all the panels are closed, open the first one..
			let expanded = this.accordionItems.filter(p => p.expanded)
			if (expanded.length > 0) {
				return
			}

			setTimeout(() => this.openFirstSection(), 0) //This force to be changed in the next render loop to avoid the error: "Expression has changed after it was checked. Previous value: 'false'. Current value: 'true'"
			//this.openFirstSection();
		})


	}

	private openFirstSection() {
		let nextExpansionPanel = this.accordionItems.get(0);
		let nextSectionToOpen = this.currentForm()!.sections[0];

		this.clickOnExpansionPanel(nextSectionToOpen!, true);

		if (nextExpansionPanel != null) {
			nextExpansionPanel.toggle();

		}
	}

	private subscribeNextEvent(section: EFormSection) {
		//Close the current section, search the next visible one and open it
		section.next.subscribe(n => {

			this.eFormScriptsService.validateSection(this.data.procedureInstance.serviceInstanceId, this.data.procedureInstance.id, section.sectionId()).subscribe(r => {
				response.onResponse(r, this.snackBar, () => {

					if (r.entity.validationResult.ok == false) {
						this.snackBar.open("" + r.entity.validationResult.messageCode, "Tancar")
						return
					}

					if (r.entity.validationResult.entity.ok == true) {
						this.closeSectionAndOpenNext()
						return
					}

					this.data.sectionErrors.putSectionValidationErrors(r.entity)
				})
			})
		});
	}

	private closeSectionAndOpenNext() {
		//Search the next panel and click
		//Search opened panel:
		let currentExpansionPanel = this.accordionItems.filter(o => o.expanded)[0];
		//Search next 
		let currentExpansionPanelIdx = 0;

		for (let index = 0; index < this.accordionItems.length; index++) {
			const element = this.accordionItems.get(index);
			if (element == currentExpansionPanel) {
				break;
			}
			currentExpansionPanelIdx++;
		}

		if (this.accordionItems.length <= currentExpansionPanelIdx + 1) {
			//Is the last one, no more sections to open
			return
		}

		currentExpansionPanel.close();

		//search the new opened section having account the section visibility in order to open it
		let openedSectionIndex = this.currentForm()!.sections.indexOf(this.openedDynFormSection()!);
		let nextSectionToOpen = this.currentForm()!.sections.find((p, idxx) => {
			if (idxx <= openedSectionIndex) {
				return false;
			}
			if (this.formSectionMetadataState()[idxx].shown == true) {
				return true;
			}
			return false;
		});

		this.clickOnExpansionPanel(nextSectionToOpen!, true);
		let nextExpansionPanel = this.accordionItems.get(currentExpansionPanelIdx + 1);
		if (nextExpansionPanel != null) {
			nextExpansionPanel.toggle(); //Open the new panel
		}

	}


	ngOnInit(): void {

		this.data.registry.register(this)

		let procedureFormId: number = this.data.procedureInstance.procedureFormId

		const getInfo = () => {

			forkJoin<[DynFormBase, { ok: boolean, entity: EFormHeaderModelValue, messageCode: string }]>(
				[this.dynFormService.findDynForm(procedureFormId),
				this.eFormValueService.getEFormHeaderModelValue(this.data.procedureInstance.serviceInstanceId, this.data.procedureInstance.id)]).
			subscribe(rb => {
				let dynForm = rb[0]
				let headerValue = rb[1]
				if (headerValue.ok == false) {
					//TODO: DO something
					console.log("Header recover error")
					this.snackBar.open(`Error al carregar la capçalera: ${this.data.procedureInstance.serviceInstanceId} - ${this.data.procedureInstance.id}`)
					return
				}

				let calls: Observable<SectionMetadata>[] = []
				dynForm.sections.forEach(s => {
					calls.push(this.eFormSectionMetadataService.find(s))
				})

				forkJoin(calls).subscribe(m => {
					this.currentForm.set(dynForm)
					this.formSectionMetadataState.set(m)

					if (headerValue.entity == null) {
						//It's the first time, so we init the header values 
						this.storeEFormHeaderModelValue(m);
					} else {
						//Merge the metadata with values EFormHeaderModelValue stored into the S3
						this.mergeFormSectionMetadataState(headerValue);
					}

					this.loadAllPosibleSectionChangeVisibility(dynForm)

				})

				this.loadAllSectionValues(dynForm);

			})

		}

		if (this.data.registry.secretInitialize()) {
			getInfo()
			return
		}

		this.procedureInstanceVisibilityService.check([this.data.procedureInstance]).subscribe(r => {
			getInfo()
		})
	}

	ngOnDestroy(): void {
		this.data.registry.unRegister(this)
	}

	private mergeFormSectionMetadataState(headerValue: { ok: boolean; entity: EFormHeaderModelValue; messageCode: string; }) {
		headerValue.entity.sections.forEach(sec => {

			this.formSectionMetadataState.update((current) => {
				return current.map(item => {
					if (item.sectionId == sec.sectionId) {
						return { ...item, shown: sec.shown, validForm: sec.validForm }
					}
					return item
				})
			})
			
		});
	}

	/**
	 * 
	 * @param dynForm Preload all the values to cache them
	 */
	private loadAllSectionValues(dynForm: DynFormBase) {
		let valuesCalls: Observable<EFormSectionModelValueResponse>[] = [];
		dynForm.sections.forEach(s => {
			valuesCalls.push(this.eFormValueService.getEFormSectionModelValue(this.data.procedureInstance.serviceInstanceId, this.data.procedureInstance.id, s.id));
		});


		forkJoin(valuesCalls).subscribe(m => { });
	}

	private loadAllPosibleSectionChangeVisibility(dynForm: DynFormBase) {

		// Start empty //
		this.possibleChangeVisibilitySection().clear()
		
		dynForm.sections.forEach(s => {
			this.eFormSectionMetadataService.findMetadata(s).subscribe(metadata => {

				let visibility: (HasShowHideLogic & ItemMetadataBase)[] = []

				metadata.items.forEach(l => {
					
					if (l.type != LayoutTypeMetadata.Row) {
						return
					}
		
					(<RowLayoutMetadataBase>l).items.forEach(i => {
						if (i.type != ItemTypeMetadata.CheckBox && i.type != ItemTypeMetadata.Select && i.type != ItemTypeMetadata.Radio && i.type != ItemTypeMetadata.MultiCheckBox) {
							return
						}
		
						if (ShowHideLogicMetadataBase.hasAnyValue((<HasShowHideLogic & ItemMetadataBase>i).logicShowHideSection) == false) {
							return
						}
		
						visibility.push(<HasShowHideLogic & ItemMetadataBase>i)
					})
				})

				this.possibleChangeVisibilitySection.update((current) => {
					return current.set(metadata.id, { sectionId: s.id, logic: visibility })
				})
			})
		})

	}

	private storeEFormHeaderModelValue(m: SectionMetadata[]) {
		let h: EFormHeaderModelValue = {
			serviceInstanceId: this.data.procedureInstance.serviceInstanceId,
			procedureInstanceId: this.data.procedureInstance.id,
			sections: m
		};
		this.eFormValueService.putEFormHeaderModelValue(h).subscribe(rr => {
			if (rr.ok == false) {
				//TODO: do something
				console.log("Error saving header");
			}
		});
	}

	clickOnExpansionPanel(section: DynFormSection, expanded: boolean) {

		if (expanded == false || (this.openedDynFormSection() != null && this.openedDynFormSection()!.id == section.id)) {
			return
		}

		if (expanded == true && (this.openedDynFormSection() != null && this.openedDynFormSection()!.id == section.id)) {
			return
		}

		this.openedComponentSection.set(null)
		this.openedDynFormSection.set(null)
		this.eFormFactoryService.newEFormSection(section, this.data.procedureInstance, this.data.sectionErrors).subscribe(s => {
			this.openedComponentSection.set(s)
			this.openedDynFormSection.set(section)
		})

	}
	executeStoreJob = (data: StoreJob): Observable<{ ok: boolean, messageCode: string }> => {

		//Check if has been changes on visibility:
		let hasVisibilityChanged = this.applySectionsVisibilityChanges(data)
		let hasValidFormChanged = this.applySectionsFormValidChanges(data)

		let eFormSectionModelValue: EFormSectionModelValue = {
			sectionId: data.value.sectionId,
			value: data.value.value
		}

		if (hasVisibilityChanged == false && hasValidFormChanged == false) {
			return this.eFormValueService.putEFormSectionModelValue(data.serviceInstanceId, data.procedureInstanceId, eFormSectionModelValue)
		}

		let both: EFormHeaderAndOneSectionModelValue = {
			header: { procedureInstanceId: data.procedureInstanceId, serviceInstanceId: data.serviceInstanceId, sections: this.formSectionMetadataState() },
			section: eFormSectionModelValue
		}
		
		return this.eFormValueService.putEFormHeaderAndOneSectionModelValue(data.serviceInstanceId, data.procedureInstanceId, both)
	}

	applySectionsFormValidChanges(storeJob: StoreJob): boolean {

		let hasBeenChanged: boolean = false
		this.formSectionMetadataState.update((current) => {
			return current.map(item => {
				if (item.sectionId === storeJob.value.sectionId && item.validForm != storeJob.validForm) {
					hasBeenChanged = true
					return { ...item, validForm: storeJob.validForm}
				}
				return item
			})
		})

		return hasBeenChanged
	}

	applySectionsVisibilityChanges(vc: StoreJob): boolean {

		let hasBeenChanged: boolean = false

		this.getModifiedVisiblity(vc.value).forEach(p => {
			this.formSectionMetadataState.update((current) => {
				return current.map(item => {
					if (item.identifier === p.identifier && item.shown != p.show) {
						hasBeenChanged = true
						return { ...item, shown: p.show}
					}
					return item				
				})
			})
		})

		return hasBeenChanged
	}

	getModifiedVisiblity(eFormSectionValue: EFormSectionModelValue): EFormSectionVisibility[] {

		const visibilityMap: Map<string, boolean> = new Map();

		const changeVisibility = (identifier: string, show: boolean) => {
			visibilityMap.set(identifier, show);
		};

		const shouldShowSection = (item: (HasShowHideLogic & ItemMetadataBase), s: ShowHideLogicMetadata, value: any) => {
			let val = value[item.fieldName];
			if (item.type === 'MULTI_CHECKBOX_ITEM' && val !== undefined) {
				val = val[<string> s.value];
			}
			return (item.type === 'MULTI_CHECKBOX_ITEM' && val) || value[item.fieldName] === s.value;
		};

		const propagateVisibilityChange = (identifier: string, value: any) => {
			const sectionLogic = this.possibleChangeVisibilitySection().get(identifier);
			if (sectionLogic?.logic?.length) {
				sectionLogic.logic.forEach(item => {
					item.logicShowHideSection.forEach(s => {
						const newShow = shouldShowSection(item, s, value);
						changeVisibility(s.ref!, newShow);  // Cambiar visibilidad

						// Propagación recursiva solo si la sección se hace visible
						if (newShow) {
							const sectionValue = this.eFormValueService.getEFormSectionModelValueCached(
								this.data.procedureInstance.serviceInstanceId,
								this.data.procedureInstance.id,
								sectionLogic.sectionId
							);
							if (sectionValue && sectionValue.entity.value) {
								propagateVisibilityChange(s.ref!, sectionValue.entity.value);  // Recursión
							}
						}
					});
				});
			}
		};

		const validateVisibilityLogic = (identifier: string, value: any): EFormSectionVisibility[] => {
			const sectionLogic = this.possibleChangeVisibilitySection().get(identifier);
			if (!sectionLogic?.logic?.length) return [];

			const visibility: EFormSectionVisibility[] = [];
			sectionLogic.logic.forEach(item => {
				item.logicShowHideSection.forEach(s => {
					const show = shouldShowSection(item, s, value);
					visibility.push({ identifier: s.ref!, show });
				});
			});
			return visibility;
		};

		const currentChange = this.formSectionMetadataState().find(s => s.sectionId === eFormSectionValue.sectionId);
    	if (!currentChange) {
			return [];
		}

		const visibility = validateVisibilityLogic(currentChange.identifier!, eFormSectionValue.value);

		this.formSectionMetadataState().forEach(s => {
			const sectionVisibility = visibility.find(v => v.identifier === s.identifier);
			if (sectionVisibility && sectionVisibility?.show !== s.shown) {
				changeVisibility(s.identifier!, sectionVisibility.show);
				if (sectionVisibility.show) {
					// Si la sección se hace visible, validamos sus dependencias
					const sectionValue = this.eFormValueService.getEFormSectionModelValueCached(
						this.data.procedureInstance.serviceInstanceId,
						this.data.procedureInstance.id,
						s.sectionId
					);
					if (sectionValue && sectionValue.entity.value) {
						propagateVisibilityChange(s.identifier!, sectionValue.entity.value);
					}
				} else {
					// Si la sección se oculta, propagamos la ocultación
					propagateVisibilityChange(s.identifier!, eFormSectionValue.value);
				}
			}
		});

		return Array.from(visibilityMap.entries()).map(([identifier, show]) => ({ identifier, show }))
	}

	isValid(): Observable<string | null> {

		//Check that all the sections are valid, if one section is missing we have to initialize with a default value and check it ...
		let invalidSection: string | null = null;
		this.formSectionMetadataState().forEach(r => {
			if (r.shown == false) {
				return
			}

			if (r.validForm == null) {
				//TODO: Should check if the section is mandatory or not. If not mandatory, the update the default values and go ahead.
				return
			}

			if (r.validForm == false) {
				let section = this.currentForm()!.sections.filter(p => p.id == r.sectionId)
				if (section.length == 0) {
					invalidSection = "Secció desconueguda"
					return
				}
				invalidSection = section[0].name
				return
			}
		})

		return of(invalidSection)
	}

	isFirstSection(index: number): boolean {
		if (index == 0 && this.formSectionMetadataState()[index].shown == true) {
			return true
		}

		if (index > 0 && this.formSectionMetadataState()[index].shown == true && this.formSectionMetadataState().filter((e, i) => i < index && e.shown == false).length > 0) {
			//return true
			return false
		}
		return false
	}

	isLastSection(index: number): boolean {

		if (index > 0 && this.formSectionMetadataState()[index].shown == true && this.formSectionMetadataState().filter((e, i) => i > index && e.shown == true).length == 0) {
			return true
		}

		if (index == 0 && this.formSectionMetadataState()[index].shown == true && this.formSectionMetadataState().length == 1) {
			return true
		}

		return false
	}
}
